import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { api, API } from "../lib/api";
import { useState } from "react";
import { Download, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";

// Sample PDF page previews are baked into the frontend build as static assets
// at /sample/page-{N}.png. This removes the runtime dependency on `poppler-utils`
// on the production container (the backend's /api/sample-pdf/page/{n} endpoint
// requires it to render PDF → PNG, which fails in some deploy environments).
// If the sample PDF template changes, regenerate these PNGs and commit them.
const SAMPLE_PAGES = [
  "/sample/page-1.png",
  "/sample/page-2.png",
  "/sample/page-3.png",
];

export default function Sample() {
  const samplePdfUrl = `${api.samplePdfUrl()}?t=${Date.now()}`;
  const [fallbackUsed, setFallbackUsed] = useState({});

  return (
    <Layout>
      <SEO
        title="Sample Bill of Sale | CarSaleDoc"
        description="Sample preview of the bill of sale PDF."
        path="/sample-pdf"
        noindex
      />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Sample bill of sale
            </h1>
            <p className="text-[var(--csd-text-secondary)] mt-2 text-sm">
              A fully signed example with the SAMPLE watermark — exactly what
              your final PDF will look like.
            </p>
          </div>
          <div className="flex gap-2">
            <a
              href={samplePdfUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="sample-open-tab"
            >
              <Button variant="outline" className="rounded-xl">
                <ExternalLink size={14} className="mr-2" />
                Open in new tab
              </Button>
            </a>
            <a
              href={samplePdfUrl}
              download="sample_bill_of_sale.pdf"
              data-testid="sample-download-btn"
            >
              <Button className="csd-btn-primary rounded-xl">
                <Download size={14} className="mr-2" />
                Download PDF
              </Button>
            </a>
          </div>
        </div>

        <div className="space-y-6" data-testid="sample-pdf-pages">
          {SAMPLE_PAGES.map((src, i) => {
            const finalSrc = fallbackUsed[i]
              ? `${API}/sample-pdf/page/${i + 1}`
              : src;
            return (
              <div
                key={i}
                className="csd-card overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
              >
                <img
                  src={finalSrc}
                  alt={`Sample bill of sale page ${i + 1}`}
                  className="w-full block"
                  loading={i === 0 ? "eager" : "lazy"}
                  data-testid={`sample-page-${i + 1}`}
                  onError={() => {
                    // Static asset missing → try the live backend endpoint
                    // as a last resort. If THAT also fails the broken-image
                    // icon will appear, which is at least obvious to debug.
                    if (!fallbackUsed[i]) {
                      setFallbackUsed((m) => ({ ...m, [i]: true }));
                    }
                  }}
                />
                <div className="text-xs text-[var(--csd-text-secondary)] text-center py-2 bg-gray-50 border-t border-[var(--csd-border)]">
                  Page {i + 1} of {SAMPLE_PAGES.length}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-[var(--csd-text-secondary)] mt-8 text-center leading-relaxed max-w-2xl mx-auto">
          This is a sample document with a SAMPLE watermark. Your finalized
          bill of sale will be delivered without the watermark and signed by
          both parties.
        </p>
      </div>
    </Layout>
  );
}
